import { StatusCodes } from 'http-status-codes'
import { Err, Ok, Result } from 'ts-results'

import { HttpError } from './httpError'

export const constructHeaders = (contentType?: string) => {
  const header: Record<string, string> = {}

  if (contentType) {
    header['Content-Type'] = contentType
  }

  return header
}

const handleResponse = async <R, E>(response: Response): Promise<Result<R, HttpError<E>>> => {
  if (response.ok) {
    switch (response.status as StatusCodes) {
      case StatusCodes.CREATED:
        return Promise.resolve(Ok(response.headers.get('Location') as R))
      case StatusCodes.NO_CONTENT:
        return Promise.resolve(Ok(undefined as R))
      default: {
        const responseContentType = response.headers.get('content-type')
        if (responseContentType) {
          if (responseContentType.startsWith('application/json')) {
            return Ok(await response.json())
          }

          // as there are multiple types, which return blob data we generally use this....
          return Ok((await response.blob()) as R)
        }

        return Ok((await response.text()) as R)
      }
    }
  } else {
    const responseContentType = response.headers.get('content-type')
    if (responseContentType?.startsWith('application/json')) {
      const errorResponse = (await response.json()) as E

      if (errorResponse) {
        return Err(new HttpError(response.status, response.statusText, errorResponse))
      }
    }

    return Err(new HttpError(response.status, response.statusText))
  }
}

export const httpCall = <T, R, E>(
  url: string,
  method = 'GET',
  contentType?: string,
  data?: T | BodyInit,
) =>
  fetch(url, {
    credentials: 'include',
    method,
    headers: constructHeaders(contentType),
    body: data
      ? contentType === 'application/json' && typeof data !== 'string'
        ? JSON.stringify(data)
        : (data as BodyInit)
      : undefined,
  })
    .then(handleResponse<R, E>)
    .catch((e: Error) =>
      Err(new HttpError(0, 'Unkown error occured: ' + e.message, undefined, e) as HttpError<E>),
    )
